<template lang="html">
<div class="se-page">
    <header class="se-hero">
        <div>
            <div class="se-kicker">SMARTLINKS</div>
            <h1>Search Entities</h1>
            <p>Find offers and global offers across every Smartlink, and pause them where they run.</p>
        </div>
    </header>

    <section class="se-card">
        <div class="se-grid-3">
            <div class="se-field">
                <label class="se-label">Offer name</label>
                <input v-model="filterByName" type="text" class="se-input" placeholder="Filter by name" @keyup.enter="search">
            </div>

            <div class="se-field">
                <label class="se-label">Offer affise ID</label>
                <input v-model.number="filterByAffiseId" type="number" class="se-input se-input-mono" placeholder="Filter by affise ID" @keyup.enter="search">
            </div>

            <div class="se-field">
                <label class="se-label">Global offer internal ID</label>
                <input v-model.number="filterByGlobalId" type="number" class="se-input se-input-mono" placeholder="Filter by internal ID" @keyup.enter="search">
            </div>
        </div>

        <p class="se-hint">Fill more than one field to get a separate table for each search.</p>

        <button type="button" class="se-btn se-btn--ok se-search-btn" :disabled="loading" @click="search">
            {{ loading ? 'Searching...' : 'Search' }}
        </button>
    </section>

    <div v-if="loading" class="se-loading">Loading...</div>

    <template v-else-if="searched">
        <section v-for="group in resultGroups" :key="group.key" class="se-card se-results">
            <div class="se-results-header">
                <h2>{{ group.title }}</h2>
                <span class="se-badge">{{ group.rows.length }} result(s)</span>
            </div>

            <p v-if="group.rows.length === 0" class="se-empty">No results</p>

            <table v-else class="se-table">
                <thead>
                    <tr>
                        <th v-if="group.key === 'global'" class="se-col-toggle"></th>
                        <th>ID</th>
                        <th>Offer name</th>
                        <th>Affise ID</th>
                        <th>Type</th>
                        <th>Offer page</th>
                    </tr>
                </thead>
                <tbody v-for="row in group.rows" :key="group.key + '-' + row.id">
                    <tr class="se-row">
                        <td v-if="group.key === 'global'" class="se-col-toggle">
                            <button
                                v-if="usageCount(row.id) > 0"
                                type="button"
                                class="se-toggle"
                                :title="isOpen(row.id) ? 'Hide Smartlinks' : 'Show Smartlinks'"
                                @click="toggleUsages(row.id)"
                            >
                                <i
                                    class="fa fa-caret-down se-caret"
                                    :class="{ 'se-caret--open': isOpen(row.id) }"
                                    aria-hidden="true"
                                ></i>
                            </button>
                        </td>
                        <td><span class="se-id">#{{ row.id }}</span></td>
                        <td>{{ row.name }}</td>
                        <td class="se-mono">{{ row.affise_id }}</td>
                        <td><span class="se-badge">{{ offerTypeLabel(row) }}</span></td>
                        <td>
                            <a class="se-link" :href="offerPageHref(row)" target="_blank" rel="noopener">{{ offerPageLabel(row) }}</a>
                        </td>
                    </tr>

                    <tr v-if="group.key === 'global' && isOpen(row.id)">
                        <td colspan="6" class="se-usages-cell">
                            <div class="se-subcard">
                                <div class="se-subcard-title">Used by {{ usageCount(row.id) }} ad set(s)</div>

                                <table class="se-table se-table--inner">
                                    <thead>
                                        <tr>
                                            <th class="se-col-check">
                                                <input
                                                    type="checkbox"
                                                    :checked="allSelected(row.id)"
                                                    :disabled="pausableIds(row.id).length === 0"
                                                    title="Select all"
                                                    @change="toggleAll(row.id, $event.target.checked)"
                                                >
                                            </th>
                                            <th>SL ID</th>
                                            <th>SL name</th>
                                            <th>Ad set ID</th>
                                            <th>Offer ID</th>
                                            <th>Offer name</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="usage in usages[row.id].items" :key="usage.offer_id" class="se-row">
                                            <td class="se-col-check">
                                                <input
                                                    type="checkbox"
                                                    :value="usage.offer_id"
                                                    :disabled="usages[row.id].busy || !canPause(usage)"
                                                    :title="pauseTitle(usage)"
                                                    v-model="usages[row.id].selected"
                                                >
                                            </td>
                                            <td><span class="se-id">#{{ usage.sl_id }}</span></td>
                                            <td>
                                                <a class="se-link" :href="slHref(usage)" target="_blank" rel="noopener">{{ usage.sl_name }}</a>
                                            </td>
                                            <td>
                                                <a class="se-link" :href="adSetHref(usage)" target="_blank" rel="noopener">#{{ usage.ad_set_id }}</a>
                                            </td>
                                            <td class="se-mono">{{ usage.offer_id }}</td>
                                            <td>{{ usage.offer_name }}</td>
                                        </tr>
                                    </tbody>
                                </table>

                                <button
                                    v-if="usages[row.id].selected.length > 0"
                                    type="button"
                                    class="se-btn se-btn--danger"
                                    :disabled="usages[row.id].busy"
                                    @click="pause(row.id)"
                                >
                                    Pause selected ({{ usages[row.id].selected.length }})
                                </button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </section>
    </template>
</div>
</template>

<script>
export default {
    name: 'SearchEntities',
    data() {
        return {
            filterByName: '',
            filterByAffiseId: 0,
            filterByGlobalId: 0,
            loading: false,
            searched: false,
            applied: { name: '', affiseId: 0, globalId: 0 },
            byName: [],
            byAffiseId: [],
            byGlobalId: [],
            usages: {}
        }
    },
    computed: {
        activeFilters() {
            const filters = []
            if (this.applied.name !== '') {
                filters.push({ key: 'name', title: `Offers matching name "${this.applied.name}"` })
            }
            if (this.applied.affiseId) {
                filters.push({ key: 'affise', title: `Offers matching affise ID ${this.applied.affiseId}` })
            }
            if (this.applied.globalId) {
                filters.push({ key: 'global', title: `Global offers matching internal ID ${this.applied.globalId}` })
            }
            return filters
        },
        resultGroups() {
            return this.activeFilters.map(filter => ({
                key: filter.key,
                title: filter.title,
                rows: this.rowsFor(filter.key)
            }))
        }
    },
    methods: {
        rowsFor(key) {
            if (key === 'name') {
                return this.byName
            }
            if (key === 'affise') {
                return this.byAffiseId
            }
            return this.byGlobalId
        },
        async search(e) {
            if (e) {
                e.preventDefault()
            }

            const applied = {
                name: this.filterByName,
                affiseId: this.filterByAffiseId,
                globalId: this.filterByGlobalId
            }
            if (applied.name === '' && !applied.affiseId && !applied.globalId) {
                this.notifyError('Fill at least one filter.')
                return
            }

            this.loading = true
            this.usages = {}
            try {
                this.byName = applied.name !== '' ? await this.runSearch({ name: applied.name }) : []
                this.byAffiseId = applied.affiseId ? await this.runSearch({ affise_id: String(applied.affiseId) }) : []
                this.byGlobalId = applied.globalId ? await this.runSearch({ global_offer_id: String(applied.globalId) }) : []
                await this.loadUsages(this.byGlobalId)
                this.applied = applied
                this.searched = true
            } catch (err) {
                this.notifyError(err)
            } finally {
                this.loading = false
            }
        },
        async runSearch(filter) {
            const response = await this.$http.post(this.resources.SmartLinksCampaigns.filterOffersByNameOrID, {
                name: filter.name || '',
                affise_id: filter.affise_id || '0',
                global_offer_id: filter.global_offer_id || '0'
            })
            return await response.json() || []
        },
        async loadUsages(rows) {
            const entries = await Promise.all(rows.map(async row => {
                try {
                    const response = await this.$http.get(this.resources.SmartLinksCampaigns.getGlobalOfferUsages(row.id))
                    return { id: row.id, items: await response.json() || [] }
                } catch (err) {
                    return { id: row.id, items: [] }
                }
            }))

            const usages = {}
            entries.forEach(entry => {
                usages[entry.id] = { open: false, busy: false, items: entry.items, selected: [] }
            })
            this.usages = usages
        },
        usageCount(globalOfferId) {
            return this.usages[globalOfferId] ? this.usages[globalOfferId].items.length : 0
        },
        isOpen(globalOfferId) {
            return !!this.usages[globalOfferId] && this.usages[globalOfferId].open
        },
        toggleUsages(globalOfferId) {
            this.usages[globalOfferId].open = !this.usages[globalOfferId].open
        },
        canPause(usage) {
            return usage.weight > 0 && !usage.is_rotating
        },
        pauseTitle(usage) {
            if (usage.is_rotating) {
                return 'This ad set is on offer rotation'
            }
            if (usage.weight === 0) {
                return 'Already paused'
            }
            return ''
        },
        pausableIds(globalOfferId) {
            return this.usages[globalOfferId].items.filter(this.canPause).map(v => v.offer_id)
        },
        allSelected(globalOfferId) {
            const pausable = this.pausableIds(globalOfferId)
            return pausable.length > 0 && pausable.length === this.usages[globalOfferId].selected.length
        },
        toggleAll(globalOfferId, checked) {
            this.usages[globalOfferId].selected = checked ? this.pausableIds(globalOfferId) : []
        },
        async pause(globalOfferId) {
            const entry = this.usages[globalOfferId]
            entry.busy = true
            try {
                const response = await this.$http.post(this.resources.SmartLinksCampaigns.pauseOfferWeights, {
                    offer_ids: entry.selected
                })
                const result = await response.json()

                const paused = result.paused || []
                entry.items.forEach(item => {
                    if (paused.indexOf(item.offer_id) !== -1) {
                        item.weight = 0
                    }
                })
                entry.selected = []

                this.notifySuccess(`Paused ${paused.length} offer(s).`)

                const autoPaused = result.auto_paused_sls || []
                if (autoPaused.length > 0) {
                    this.notifySuccess(`Auto-paused Smartlink(s): ${autoPaused.join(', ')}`)
                }

                const skipped = result.skipped || []
                if (skipped.length > 0) {
                    this.notifyError(`Skipped ${skipped.length} offer(s): ${skipped[0].reason}`)
                }

                const failed = result.failed_sls || []
                if (failed.length > 0) {
                    this.notifyError(`Could not auto-pause Smartlink(s): ${failed.join(', ')}. Check them manually.`)
                }
            } catch (err) {
                this.notifyError(err)
            } finally {
                entry.busy = false
            }
        },
        slHref(usage) {
            return '/apps/smartlinks/campaigns/' + usage.sl_id
        },
        adSetHref(usage) {
            return '/apps/smartlinks/campaigns/' + usage.sl_id + '/placements/' + usage.ad_set_id
        },
        offerTypeLabel(row) {
            if (row.is_new_global_offer) {
                return 'New Global Offer'
            }
            if (!row.camp_id || row.camp_id === 0) {
                return 'Legacy Global Offer'
            }
            return 'Campaign Offer'
        },
        offerPageHref(row) {
            if (row.camp_id && row.camp_id !== 0) {
                return '/apps/smartlinks/campaigns/' + row.camp_id + '/placements/' + row.placement_id + '/offer/' + row.id
            }
            if (row.is_new_global_offer) {
                return '/apps/smartlinks/new-global-offers/create?id=' + row.id
            }
            return '/apps/smartlinks/global-offers-edit?id=' + row.id + '&isVta=' + (row.ctr_id != 0 ? 1 : 0)
        },
        offerPageLabel(row) {
            if (row.camp_id && row.camp_id !== 0) {
                const slName = row.sl_name && row.sl_name.String ? row.sl_name.String : ''
                return 'SL/' + row.camp_id + '-' + slName
            }
            return 'Offer/' + row.id
        }
    }
}
</script>

<style scoped>
.se-page {
    min-height: 100%;
    padding: 24px;
    color: #0f172a;
    font-family: Inter, system-ui, -apple-system, sans-serif;
    box-sizing: border-box;
    text-align: left;
}

.se-hero {
    margin-bottom: 18px;
}

.se-kicker {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .5px;
    background: rgba(15, 23, 42, 0.05);
    border: 1px solid rgba(15, 23, 42, 0.12);
}

.se-hero h1 {
    margin: 8px 0 4px;
    font-size: 32px;
    line-height: 1.05;
    font-weight: 800;
}

.se-hero p {
    font-size: 0.95rem;
    color: rgba(15, 23, 42, 0.7);
    margin: 6px 0 0;
}

.se-card {
    border-radius: 20px;
    padding: 20px 24px;
    margin-bottom: 20px;
    background: rgba(255, 255, 255, 0.75);
    border: 1px solid rgba(15, 23, 42, 0.1);
    box-shadow: 0 18px 60px rgba(15, 23, 42, 0.1);
    backdrop-filter: blur(10px);
    text-align: left;
}

.se-grid-3 {
    display: grid;
    grid-template-columns: repeat(3, minmax(200px, 1fr));
    gap: 16px;
}

.se-field {
    text-align: left;
}

.se-label {
    display: block;
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #475569;
    margin-block: 10px;
}

.se-input {
    display: block;
    width: 100%;
    padding: 10px 12px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.98);
    border: 1px solid rgba(148, 163, 184, 0.55);
    color: #0f172a;
    font-size: 0.875rem;
    outline: none;
    box-sizing: border-box;
}

.se-input:focus {
    border-color: rgba(14, 165, 233, 0.85);
    box-shadow: 0 0 0 0.18rem rgba(14, 165, 233, 0.22);
}

.se-input-mono,
.se-mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.se-hint {
    font-size: 0.75rem;
    color: #64748b;
    margin: 12px 0 0;
}

.se-search-btn {
    margin-top: 16px;
    padding: 12px 24px;
    font-size: 1rem;
}

.se-loading {
    padding: 24px 0;
    color: #64748b;
}

.se-results-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 6px;
}

.se-results-header h2 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 800;
}

.se-empty {
    margin: 8px 0 0;
    font-size: 0.875rem;
    color: #64748b;
}

.se-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0 8px;
    font-size: 0.875rem;
}

.se-table thead th {
    padding: 8px 14px;
    color: #475569;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .4px;
    text-transform: uppercase;
    border: 0;
}

.se-row td {
    padding: 12px 14px;
    border-top: 1px solid rgba(15, 23, 42, 0.10);
    border-bottom: 1px solid rgba(15, 23, 42, 0.10);
    background: rgba(255, 255, 255, 0.92);
    vertical-align: middle;
}

.se-row td:first-child {
    border-left: 1px solid rgba(15, 23, 42, 0.10);
    border-top-left-radius: 14px;
    border-bottom-left-radius: 14px;
}

.se-row td:last-child {
    border-right: 1px solid rgba(15, 23, 42, 0.10);
    border-top-right-radius: 14px;
    border-bottom-right-radius: 14px;
}

.se-row:hover td {
    border-color: rgba(14, 165, 233, 0.35);
}

.se-col-toggle {
    width: 34px;
}

.se-row td.se-col-toggle {
    padding-right: 0;
}

.se-col-check {
    width: 44px;
    text-align: center;
}

.se-toggle {
    padding: 0 4px;
    border: 0;
    background: transparent;
    color: #0ea5e9;
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
}

.se-caret {
    transition: transform 0.15s;
}

.se-caret--open {
    transform: rotate(180deg);
}

.se-id {
    display: inline-flex;
    padding: 3px 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    background: rgba(15, 23, 42, 0.06);
}

.se-badge {
    display: inline-flex;
    font-size: 0.75rem;
    padding: 2px 8px;
    border-radius: 9999px;
    background: rgba(248, 250, 252, 1);
    border: 1px solid rgba(148, 163, 184, 0.5);
    color: #475569;
}

.se-link {
    color: #0ea5e9;
    text-decoration: none;
}

.se-link:hover {
    text-decoration: underline;
}

.se-btn {
    border: 0;
    border-radius: 10px;
    padding: 7px 12px;
    font-weight: 700;
    color: #fff;
    cursor: pointer;
}

.se-btn:disabled {
    opacity: .45;
    cursor: not-allowed;
}

.se-btn:not(:disabled):hover {
    filter: brightness(1.03);
}

.se-btn--ok {
    background: linear-gradient(135deg, #10b981, #22c55e);
}

.se-btn--danger {
    background: linear-gradient(135deg, #ef4444, #dc2626);
}

.se-btn--ghost {
    padding: 4px 10px;
    color: #334155;
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid rgba(148, 163, 184, 0.65);
}

.se-usages-cell {
    padding: 0 0 12px;
    background: transparent;
}

.se-subcard {
    padding: 14px 16px;
    border-radius: 14px;
    background: rgba(248, 250, 252, 0.9);
    border: 1px solid rgba(15, 23, 42, 0.08);
}

.se-subcard-title {
    margin-bottom: 4px;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: .4px;
    text-transform: uppercase;
    color: #475569;
}

.se-table--inner {
    margin-bottom: 10px;
}

@media (max-width: 1200px) {
    .se-grid-3 {
        grid-template-columns: 1fr;
    }

    .se-results {
        overflow-x: auto;
    }
}
</style>
